import { Footer } from "@/components/Footer";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CheckCircle2, ArrowRight, Code, Zap, Shield, GitBranch, Upload, Bug, Terminal, FileCode, Package, Rocket } from "lucide-react";
import { Link } from "react-router-dom";
import { WaitlistCTA } from "@/components/WaitlistCTA";

const Bolt = () => {

  return (
    <main className="min-h-screen">
      <ThemeToggle />
      <section className="py-8 px-4 border-b border-border">
        <div className="container mx-auto max-w-6xl">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">← Back to Home</Link>
        </div>
      </section>

      {/* Hero */}
      <section className="py-16 sm:py-24 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-center md:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 border border-accent/30 text-accent text-sm font-medium mb-6">
                <Zap className="w-4 h-4" />
                Platform Integration
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-foreground">
                Fix Bugs in Apps Built with Bolt
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                Auto-fix production bugs in any Bolt app. Install Zipy for error monitoring, connect your GitHub repo to Healion, and get AI-generated PRs that fix issues automatically.
              </p>
              <p className="text-sm text-muted-foreground/80 italic">
                Ship faster with Bolt — let Healion handle the production bugs so you can focus on building.
              </p>
            </div>
            <div className="relative h-[300px] rounded-xl overflow-hidden bg-muted/30 border border-border flex items-center justify-center p-8">
              <div className="flex items-center gap-6">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-16 h-16 rounded-xl bg-background border border-border flex items-center justify-center text-2xl font-bold text-foreground">⚡</div>
                  <span className="text-xs text-muted-foreground font-medium">Bolt</span>
                </div>
                <ArrowRight className="w-5 h-5 text-muted-foreground" />
                <div className="flex flex-col items-center gap-2">
                  <div className="w-16 h-16 rounded-xl bg-background border border-border flex items-center justify-center">
                    <Shield className="w-8 h-8 text-accent" />
                  </div>
                  <span className="text-xs text-muted-foreground font-medium">Zipy</span>
                </div>
                <ArrowRight className="w-5 h-5 text-muted-foreground" />
                <div className="flex flex-col items-center gap-2">
                  <div className="w-16 h-16 rounded-xl bg-background border border-border flex items-center justify-center">
                    <Bug className="w-8 h-8 text-accent" />
                  </div>
                  <span className="text-xs text-muted-foreground font-medium">Healion</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Overview */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-3xl font-bold mb-4 text-center">How It Works</h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            Three tools working together: Bolt builds your app, Zipy monitors it in production, and Healion automatically fixes the bugs Zipy catches.
          </p>
          <div className="space-y-8">
            {[
              { step: "1", title: "Build & Deploy with Bolt", desc: "Build your app with Bolt as usual. Connect your project to GitHub so your code is synced to a repository. Every time you deploy, the latest code is pushed to GitHub." },
              { step: "2", title: "Install Zipy for Error Monitoring", desc: "Add the Zipy SDK to your Bolt app. Zipy captures every error, crash, and user session in production — including stack traces and session replays." },
              { step: "3", title: "Enable Sourcemaps via Zipy CLI", desc: "Configure your build to generate sourcemaps and add the Zipy CLI postbuild script. This ensures Zipy can de-obfuscate minified errors and show the exact line of code." },
              { step: "4", title: "Connect GitHub Repo to Healion", desc: "Sign up on Healion and connect the same GitHub repository where your Bolt app code lives. Healion now has full access to your codebase for generating fixes." },
              { step: "5", title: "Trigger Fix from Zipy → Healion", desc: "When Zipy catches a production error, click 'Fix with Healion' directly from Zipy's dashboard. Healion analyzes the error, traces the root cause, and creates a PR with the fix." },
              { step: "6", title: "Review, Merge & Deploy", desc: "Review the PR that Healion created, merge it into your main branch, and deploy. If your Bolt project has GitHub sync, the fix appears automatically." },
            ].map((item) => (
              <Card key={item.step} className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-accent">{item.step}</span>
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-semibold mb-2">{item.title}</h3>
                    <p className="text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Step-by-Step Setup Guide */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-3xl font-bold mb-12 text-center">Step-by-Step Setup Guide</h2>
          <div className="space-y-10">

            {/* Step 1: Connect to GitHub */}
            <div>
              <h3 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                <GitBranch className="w-5 h-5 text-accent" />Step 1: Connect Your Bolt Project to GitHub
              </h3>
              <Card className="p-6 bg-muted/50">
                <ol className="space-y-3 list-decimal list-inside text-muted-foreground">
                  <li>Open your Bolt project</li>
                  <li>Go to <span className="font-semibold text-foreground">Settings → GitHub → Connect</span></li>
                  <li>Authorize the GitHub integration</li>
                  <li>Select your GitHub account/organization</li>
                  <li>Create or select a repository</li>
                </ol>
                <div className="mt-4 p-4 bg-background rounded-md text-sm border border-border">
                  <p className="text-muted-foreground">
                    <span className="font-semibold text-foreground">Why GitHub?</span> GitHub acts as the bridge between your Bolt app and Healion. Healion reads your code from GitHub and creates PRs there, which can then sync back to your project.
                  </p>
                </div>
              </Card>
            </div>

            {/* Step 2: Create Zipy Account */}
            <div>
              <h3 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-accent" />Step 2: Create a Zipy Account & Get Your API Key
              </h3>
              <Card className="p-6 bg-muted/50">
                <ol className="space-y-3 list-decimal list-inside text-muted-foreground">
                  <li>Go to <span className="font-semibold text-foreground">zipy.ai</span> and sign up</li>
                  <li>Create a new project for your Bolt app</li>
                  <li>Copy your <span className="font-semibold text-foreground">SDK Key</span></li>
                  <li>Copy your <span className="font-semibold text-foreground">Auth Key</span> for the CLI (found in Settings → Sourcemaps)</li>
                </ol>
              </Card>
            </div>

            {/* Step 3: Install Zipy SDK */}
            <div>
              <h3 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                <Code className="w-5 h-5 text-accent" />Step 3: Add Zipy SDK to Your Bolt App
              </h3>
              <Card className="p-6 bg-muted/50">
                <p className="text-muted-foreground mb-4">Add the Zipy SDK script to your <code className="px-1 py-0.5 rounded bg-background border border-border text-xs">index.html</code> file inside the <code className="px-1 py-0.5 rounded bg-background border border-border text-xs">&lt;head&gt;</code> tag:</p>
                <div className="p-4 bg-background rounded-md font-mono text-sm border border-border overflow-x-auto">
                  <pre className="text-foreground">{`<script src="https://cdn.zipy.ai/sdk/v1.0/zipy.min.umd.js"
  crossorigin="anonymous"></script>
<script>
  window.zipy && window.zipy.init('YOUR_SDK_KEY');
</script>`}</pre>
                </div>
                <p className="mt-4 text-sm text-muted-foreground">
                  Replace <code className="px-1 py-0.5 rounded bg-background border border-border text-xs">YOUR_SDK_KEY</code> with the API key from Step 2.
                </p>
              </Card>
            </div>

            {/* Step 4: Enable Sourcemaps */}
            <div>
              <h3 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                <FileCode className="w-5 h-5 text-accent" />Step 4: Enable Sourcemaps
              </h3>
              <Card className="p-6 bg-muted/50">
                <p className="text-muted-foreground mb-4">Update your build config to generate sourcemaps:</p>
                <div className="p-4 bg-background rounded-md font-mono text-sm border border-border overflow-x-auto">
                  <pre className="text-foreground">{`// vite.config.ts or similar build config
export default defineConfig({
  build: {
    sourcemap: true,  // ← Add this line
  },
});`}</pre>
                </div>
                <div className="mt-4 p-4 bg-background rounded-md text-sm border border-border">
                  <p className="text-muted-foreground">
                    <span className="font-semibold text-foreground">Why sourcemaps?</span> Without sourcemaps, production errors show minified code. With sourcemaps, Zipy shows the exact file, line, and function name where the error occurred.
                  </p>
                </div>
              </Card>
            </div>

            {/* Step 5: Install Zipy CLI */}
            <div>
              <h3 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-accent" />Step 5: Install Zipy CLI & Configure Postbuild Script
              </h3>
              <Card className="p-6 bg-muted/50">
                <p className="text-muted-foreground mb-4">Install the <code className="px-1 py-0.5 rounded bg-background border border-border text-xs">zipy-cli</code> package and add the postbuild script to <code className="px-1 py-0.5 rounded bg-background border border-border text-xs">package.json</code>:</p>
                <div className="p-4 bg-background rounded-md font-mono text-sm border border-border overflow-x-auto">
                  <pre className="text-foreground">{`{
  "scripts": {
    "build": "vite build",
    "postbuild": "zipy-cli --authKey YOUR_AUTH_KEY --dirname ./dist --apiKey YOUR_SDK_KEY --releaseVer 1.0.0"
  }
}`}</pre>
                </div>
                <div className="mt-4 p-4 bg-background rounded-md text-sm border border-border">
                  <p className="text-muted-foreground">
                    <span className="font-semibold text-foreground">How it works:</span> Every time your app is built, the postbuild script automatically uploads sourcemaps to Zipy. This happens seamlessly with every deploy.
                  </p>
                </div>
              </Card>
            </div>

            {/* Step 6: Sign Up on Healion */}
            <div>
              <h3 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                <Bug className="w-5 h-5 text-accent" />Step 6: Sign Up on Healion & Connect GitHub
              </h3>
              <Card className="p-6 bg-muted/50">
                <ol className="space-y-3 list-decimal list-inside text-muted-foreground">
                  <li>Go to <span className="font-semibold text-foreground">healion.ai</span> and sign up</li>
                  <li>Connect the <span className="font-semibold text-foreground">same GitHub repository</span> where your Bolt app code lives</li>
                  <li>Healion will index your codebase for AI-powered root cause analysis</li>
                  <li>Configure your preferences (target branch, auto-PR settings, etc.)</li>
                </ol>
              </Card>
            </div>

            {/* Step 7: Trigger Fix */}
            <div>
              <h3 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-accent" />Step 7: Fix Bugs from Zipy → Healion
              </h3>
              <Card className="p-6 bg-muted/50">
                <ol className="space-y-3 list-decimal list-inside text-muted-foreground">
                  <li>When a production error occurs, Zipy captures it with full context</li>
                  <li>Open the error in Zipy's dashboard — view the session replay and stack trace</li>
                  <li>Click <span className="font-semibold text-foreground">"Fix with Healion"</span> directly from the Zipy error detail page</li>
                  <li>Healion analyzes the error, traces the root cause in your codebase, and generates a fix</li>
                  <li>A PR is created on your GitHub repo with the fix</li>
                  <li>Review the fix, merge, and deploy</li>
                </ol>
              </Card>
            </div>

          </div>
        </div>
      </section>

      {/* Pro Tips */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-3xl font-bold mb-12 text-center">Pro Tips for Bolt Developers</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                icon: Terminal,
                title: "Version Your Releases",
                desc: "Update the releaseVer in your postbuild script with each major deploy (e.g., 1.0.0 → 1.1.0). This helps Zipy and Healion correlate errors to specific versions of your app."
              },
              {
                icon: Shield,
                title: "Identify Users in Zipy",
                desc: "Call window.zipy.identify(userId, { email }) after login to tie session replays to real users. This gives Healion richer context for prioritizing and fixing bugs."
              },
              {
                icon: GitBranch,
                title: "Keep GitHub in Sync",
                desc: "Make sure your GitHub repo always has the latest code from your Bolt project. This ensures Healion's fixes are based on the current codebase."
              },
              {
                icon: Upload,
                title: "Sourcemaps Are Automatic",
                desc: "Once configured, sourcemaps upload with every build. You don't need to do anything extra — the postbuild script handles it silently."
              },
              {
                icon: Bug,
                title: "Don't Ignore Console Errors",
                desc: "Zipy captures console errors too. Even warnings can indicate deeper issues. Let Healion analyze them — you might catch bugs before users report them."
              },
              {
                icon: Rocket,
                title: "Full Loop in Minutes",
                desc: "Error occurs → Zipy captures it → You trigger Healion → PR created → You review and merge → Deploy. The entire cycle can take under 15 minutes."
              },
            ].map((tip, i) => (
              <Card key={i} className="p-6">
                <tip.icon className="w-8 h-8 text-accent mb-4" />
                <h3 className="font-heading text-lg font-semibold mb-2">{tip.title}</h3>
                <p className="text-muted-foreground text-sm">{tip.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Architecture Diagram */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-3xl font-bold mb-8 text-center">Architecture Overview</h2>
          <Card className="p-8">
            <div className="font-mono text-sm text-muted-foreground space-y-2">
              <p className="text-foreground font-semibold mb-4">The Complete Bug-Fix Loop:</p>
              <div className="space-y-1 overflow-x-auto">
                <p>┌─────────────┐     deploy       ┌──────────┐</p>
                <p>│    Bolt     │ ──────────────▶  │  GitHub  │</p>
                <p>│   (Build)   │ ◀──────────────  │  (Repo)  │</p>
                <p>└─────────────┘    merge PR back  └──────────┘</p>
                <p>       │                               │</p>
                <p>       │ postbuild                     │ codebase</p>
                <p>       ▼                               ▼</p>
                <p>┌─────────────┐   fix trigger   ┌──────────┐</p>
                <p>│    Zipy     │ ──────────────▶  │ Healion  │</p>
                <p>│ (Monitor)   │                  │ (AI Fix) │</p>
                <p>└─────────────┘                  └──────────┘</p>
                <p>  sourcemaps +                    creates PR</p>
                <p>  error capture                   on GitHub</p>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="container mx-auto max-w-2xl text-center">
          <h2 className="font-heading text-3xl font-bold mb-6">Ready to Auto-Fix Bugs in Your Bolt App?</h2>
          <p className="text-muted-foreground mb-8">Join the waitlist and start fixing production bugs automatically</p>
          <WaitlistCTA source="Bolt Page" layout="stacked" />
        </div>
      </section>
      <Footer />
    </main>
  );
};

export default Bolt;
